import {
  DialogModal,
  Grid,
  IconButton,
  Icons,
  Menu,
  Popover,
  Text,
  useToggle,
} from "@pushpress/shared-components";
import React, { useMemo, useRef, useState } from "react";
import {
  GENERAL_CATEGORY_UUID,
  UNCATEGORIZED_CATEGORY,
  combineSubscriptionPlansByCategory,
} from "../Plans.utils";
import { useDeletePlanCategoryMutation } from "../../../apis/subscriptionApi.hooks";
import { usePlans } from "../PlansProvider/PlansProvider";
import styles from "./PlanList.module.scss";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { GetPlansResult, PlanCategory } from "../../../apis/subscriptionApi";
import PlanRow from "./PlanRow";

function ActivePlanList({
  categories,
  plans,
  refetchCategories,
  refetchPlans,
}: {
  categories: PlanCategory[];
  plans: GetPlansResult["data"];
  refetchCategories: VoidFunction;
  refetchPlans: VoidFunction;
}) {
  const { startPlanCategoryEdition } = usePlans();

  const [menuCategoryVisible, menuCategoryActions] = useToggle(false);

  const [deleteCategoryVisible, deleteCategoryActions] = useToggle(false);

  const [selectedPlanCategoryUuid, setSelectedPlanCategoryUuid] =
    useState<string>();

  const actionCategoryButtonRef = useRef<HTMLElement | null>(null);

  const deleteCategoryMutation = useDeletePlanCategoryMutation();

  const categoriesAndPlans = useMemo(() => {
    const activePlans = plans.filter((plan) => plan.active);
    return combineSubscriptionPlansByCategory(categories, activePlans);
  }, [categories, plans]);

  const handleEditCategory = (planCategoryUuid: string) => {
    startPlanCategoryEdition({
      planCategoryUuid,
      onSuccess: refetchCategories,
    });
    menuCategoryActions.off();
  };

  const confirmDeleteCategory = (planCategoryUuid: string) => {
    setSelectedPlanCategoryUuid(planCategoryUuid);
    deleteCategoryActions.on();
    menuCategoryActions.off();
  };

  const handleDeleteCategory = async () => {
    // check if the category has plans
    const item = categoriesAndPlans.find(
      (item) => item.category.uuid === selectedPlanCategoryUuid,
    );
    const activePlans = item?.plans.filter((plan) => plan.active);
    if (activePlans.length > 0) {
      toast.error(
        "You must move active plans to a new category before this category can be deleted",
        defaultToastOptions,
      );
      return;
    }

    try {
      await deleteCategoryMutation.mutateAsync({
        planCategoryUuid: selectedPlanCategoryUuid,
      });
      refetchCategories();
      toast.success("Category deleted successfully", defaultToastOptions);
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
    setSelectedPlanCategoryUuid(undefined);
  };

  return (
    <>
      {categoriesAndPlans.map((item) => {
        return (
          <div
            key={item.category.uuid}
            role="group"
            aria-labelledby={`label-${item.category.uuid}`}
          >
            <div className={styles.listHeader} data-cy="list-item-category">
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                className="m-0 p-0"
              >
                <Grid item className="m-0 p-0">
                  <Text
                    variant="body-lg"
                    color="wolf-800"
                    weight="600"
                    id={`label-${item.category.uuid}`}
                  >
                    {item.category.name}
                    {item.category.active === false ? " (deleted)" : ""}
                  </Text>
                </Grid>
                <Grid item className="m-0 p-0 pr-2">
                  {shouldShowTheMenu(item.category) && (
                    <IconButton
                      icon={Icons.Kebabs}
                      data-cy="list-item-action"
                      aria-label={`${item.category.name} actions`}
                      onClick={(event) => {
                        actionCategoryButtonRef.current =
                          event.target as HTMLElement;
                        menuCategoryActions.on();
                        setSelectedPlanCategoryUuid(item.category.uuid);
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            </div>
            <ul className={styles.list}>
              {item.plans.map((plan) => {
                return (
                  <PlanRow
                    key={plan.uuid}
                    plan={plan}
                    refetchPlans={refetchPlans}
                  />
                );
              })}
            </ul>
          </div>
        );
      })}
      <Popover
        anchorEl={actionCategoryButtonRef.current!}
        visible={menuCategoryVisible}
        onClose={menuCategoryActions.off}
      >
        <Menu>
          <Menu.Item
            label="Edit"
            action={() => handleEditCategory(selectedPlanCategoryUuid)}
          />
          <Menu.Item
            label="Delete"
            action={() => confirmDeleteCategory(selectedPlanCategoryUuid)}
          />
        </Menu>
      </Popover>
      <DialogModal
        visible={deleteCategoryVisible}
        title="Are you sure you want to delete this category?"
        loading={deleteCategoryMutation.isLoading}
        primaryHandler={handleDeleteCategory}
        primaryBtnTitle="Delete"
        secondaryBtnTitle="Go Back"
        secondaryHandler={deleteCategoryActions.off}
        closeModal={deleteCategoryActions.off}
      />
    </>
  );
}

function shouldShowTheMenu(category: PlanCategory) {
  return (
    category.uuid !== UNCATEGORIZED_CATEGORY &&
    category.uuid !== GENERAL_CATEGORY_UUID &&
    category.active !== false
  );
}

export default ActivePlanList;
